import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretDown,
  faCaretUp,
  faSearch,
  faCheck,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import '../../css/common/dropDown.scss';
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Typography } from "@mui/material";
import { useDebounce } from "./useDebounce";
import { Arrow, ArrowUp, focusElemnt } from "./helper";
import { Loader } from "./loader";
import { Portal } from '@mui/material';

const MultiSelectDropDownV2 = forwardRef((props, ref) => {
  const {
      width,
      listWidth = width,
      name,
      label,
      isCompulsory,
      options,
      optionLimit = false,
      preSelectedValues = [],
      searchPlaceholder,
      disabled,
      remoteData = false,
      selectAllOption = false,
      direction = "down",
      defaultAllSelected = false,
      labelIcon = "",
      id,
      nextId,
      prevId,
      autoFocus = false,
    //   portal = false,
    listHeight="170px"
  } = props;
  const defaultValue = Object.assign([], preSelectedValues);
  const [preSelectedState, setPreSelectedState] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const [focus, setFocus] = useState(false);
  const [blur, setBlur] = useState(true);
  const [list, setList] = useState(options);
  const [selectedDisplayValue, setSelectedDisplayValue] = useState("");
  const [selectedValues, setSelectedValues] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [remoteSearchInput, setRemoteSearchInput] = useState("");
  const [filteredLists, setFilteredLists] = useState(options);
  const [loading, setLoading] = useState(false);
  const dropRef = useRef(null);
  const searchInputRef = useRef(null);
  const debouncedSearchTerm = useDebounce(remoteSearchInput, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [allSelected, setAllSelected] = useState(defaultAllSelected);
  const [cursor, setCursor] = useState(-1);
  const input1ref = useRef(null);
  const searchResultRef = useRef(null);
  const portalRef = useRef(null);
//   const [dropPortalStyle, setDropPortalStyle] = useState({ width: 100, left: 0, top: 0, right: 'auto' });

  useEffect(() => {
      if (autoFocus) {
          input1ref.current.focus();
      }
  }, []);

  useEffect(() => {
      if (cursor < 0 || cursor > filteredLists.length || !searchResultRef) {
          return () => {};
      }
      if (searchResultRef && searchResultRef.current && searchResultRef.current.children) {
          let listItems = Array.from(searchResultRef.current.children);
          listItems[cursor] && scrollIntoView(listItems[cursor].offsetTop - 30);
      }
  }, [cursor]);

  const scrollIntoView = (position) => {
      searchResultRef.current.parentNode.scrollTo({
          top: position,
          behavior: "smooth",
      });
  };

  useEffect(() => {
      setAllSelected(defaultAllSelected);
  }, [defaultAllSelected, options]);

  useEffect(() => {
      if ( preSelectedValues.length === 0) {
          setSelectedDisplayValue("");
          setAllSelected(false);
      }
      if (
          preSelectedState.length !== preSelectedValues.length &&
          preSelectedValues.length === 1
      ) {
          setAllSelected(false);
      }
      if (
          preSelectedState.length !== preSelectedValues.length &&
          preSelectedValues.length > 1 && preSelectedValues.length < options?.length
      ) {
          setAllSelected(false);
      }
      if (
          preSelectedState.length !== preSelectedValues.length &&
          preSelectedValues.length > 1 && preSelectedValues.length === options?.length
      ) {
          setAllSelected(true);
      }
      setPreSelectedState(preSelectedValues);
  }, [preSelectedValues]);

  useEffect(() => {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropRef]);

  useEffect(() => {
      const { options } = props;
      setList(options);
      if (optionLimit) {
          setFilteredLists(options.slice(0, optionLimit));
      } else {
          setFilteredLists(options);
      }
  }, [options]);

  useEffect(() => {
      if (optionLimit) {
          const startIndex = (currentPage - 1) * optionLimit;
          const endIndex = startIndex + optionLimit;
          setFilteredLists(options.slice(startIndex, endIndex));
      }
  }, [currentPage, options]);

  const debounce = (func, delay) => {
      let timeoutId;
      return function (...args) {
          const context = this;
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => func.apply(context, args), delay);
      };
  };

  const handleScroll = debounce((e) => {
      if (optionLimit) {
          const { target } = e;
          const { scrollTop, scrollHeight, clientHeight } = target;
          const bottomOffset = scrollHeight - scrollTop - clientHeight;

          if (bottomOffset < 20) {
              setCurrentPage((prevPage) => prevPage + 1);
          } else if (scrollTop === 0) {
              setCurrentPage((prevPage) => Math.max(prevPage - 2, 1));
          }
      }
  }, 0);

  useEffect(() => {
      if (defaultValue.length > 0 && options.length > 0) {
          const { isObject, primaryKey } = props;
          if (isObject) {
              let selectedItems = [];
              for (let dv of defaultValue) {
                  const selectedItem = list.find(
                      (l) => l[primaryKey] === dv[primaryKey]
                  );
                  if (selectedItem) {
                      selectedItems.push(selectedItem);
                  }
              }
              setSelectedValues(selectedItems);
          } else {
              setSelectedValues(defaultValue);
          }
      } else if (options.length > 0) setSelectedValues(defaultValue);
  }, [options, preSelectedState]);

  useEffect(() => {
      const { isObject, labelKey } = props;
      if (selectedValues.length > 0) {
          if (isObject) {
              const selectedDisplayValuesArr = selectedValues.map(
                  (item) => item[labelKey]
              );
              setSelectedDisplayValue(selectedDisplayValuesArr.join(", "));
          } else {
              setSelectedDisplayValue(selectedValues.join(", "));
          }
      } else {
          setSelectedDisplayValue("");
      }
  }, [selectedValues]);

  useEffect(() => {
      const { remoteData = false } = props;
      if (remoteData) {
          if (debouncedSearchTerm) {
              setLoading(true);
              fetchRemoteData(debouncedSearchTerm).then((options) => {
                  setLoading(false);
                  if (optionLimit) {
                      setFilteredLists(options.slice(0, optionLimit));
                  } else {
                      setFilteredLists(options);
                  }
              });
          } else {
              setFilteredLists([]);
              setLoading(false);
          }
      }
  }, [debouncedSearchTerm]);

  const fetchRemoteData = async (searchValue) => {
      const {
          fetchRemoteDataHandler = () => {
              return [];
          },
      } = props;
      let newOptions = await fetchRemoteDataHandler(searchValue);
      return newOptions;
  };

  const handleClickOutside = (e) => {
      if (!(dropRef && dropRef.current && dropRef.current.contains(e.target))) {
          setShowDrop(false);
      }
  };

  const handleDrop = (e) => {
      setSearchInput("");
      setShowDrop(true);
      setFilteredLists(list);

      const dropdownElement = dropRef.current;
      if (dropdownElement) {
          const rect = dropdownElement.getBoundingClientRect();
          const spaceBelow = window.innerHeight - rect.bottom;
          const spaceAbove = rect.top;

          if (direction === 'up' && spaceAbove > spaceBelow) {
              setShowDrop(true);
          } else {
              setShowDrop(false);
          }
      }
  };

  const onRemoveSelectedItem = (item) => {
      let index = -1;
      const { onRemove, primaryKey, isObject } = props;
      if (isObject) {
          index = selectedValues.findIndex(
              (sv) => sv[primaryKey] === item[primaryKey]
          );
      } else {
          index = selectedValues.indexOf(item);
      }
      if (index >= 0) {
          selectedValues.splice(index, 1);
          if (onRemove) onRemove(selectedValues, item);

          setSelectedValues(Object.assign([], selectedValues));
      }
  };

  const onSelectItem = (e, item) => {
      const { onSelect = () => { }, singleSelect } = props;
      e.stopPropagation();

      if (singleSelect) {
          onSingleSelect(item);
          onSelect([item], item);
          setAllSelected(false);
          return;
      }
      if (isSelectedValue(item)) {
          onRemoveSelectedItem(item);
          return;
      }
      selectedValues.push(item);
      onSelect(selectedValues, item);
      setSelectedValues(Object.assign([], selectedValues));
  };

  const onSingleSelect = (item) => {
      const { isObject, labelKey } = props;
      setSelectedValues([item]);
      setSelectedDisplayValue(isObject ? item[labelKey] : item);
      setShowDrop(false);
  };

  const updateAll = async () => {
      const { onSelect = () => { } } = props;

      const updatedSelectedValues = [...options];

      setSelectedValues(updatedSelectedValues);

      onSelect(updatedSelectedValues);
  };

  const onSelectAll = () => {
      setAllSelected(allSelected);
      setShowDrop(false);
      updateAll();
  };

  const removeSelectAll = () => {
      const { onSelect = () => { } } = props;
      setAllSelected(false);
      setShowDrop(false);
      setSelectedValues([]);
      onSelect([]);
  };

  const searchItems = (searchValue) => {
      if (remoteData) {
          setRemoteSearchInput(searchValue);
          return;
      }
      setSearchInput(searchValue);
      if (searchValue !== "") {
          const filterData = list.filter((item) => {
              return item[props.labelKey]
                  .toLowerCase()
                  .includes(searchValue.toLowerCase());
          });
          if (optionLimit) {
              setFilteredLists(filterData.slice(0, optionLimit));
          } else {
              setFilteredLists(filterData);
          }
      } else {
          setFilteredLists(Object.assign([], list));
      }
  };

  const clearSearchInput = () => {
      if (searchInputRef.current) {
          searchInputRef.current.value = "";
          searchItems("");
      }
  };

  const resetItem = () => {
      const { onRemove } = props;
      setSelectedDisplayValue("");
      setSelectedValues([]);
      if (onRemove) onRemove([]);
      setAllSelected(false);
  };

  const isSelectedValue = (item) => {
      const { isObject, primaryKey } = props;
      if (isObject) {
          return (
              selectedValues.filter((i) => i[primaryKey] === item[primaryKey])
                  .length > 0
          );
      }
      return selectedValues.filter((i) => i === item).length > 0;
  };

  useImperativeHandle(ref, () => ({
      resetAutocomplete(event) {
          resetItem();
      },
  }));

  const showAllLabel = () => {
      const { showAllLabel = false, labelKey } = props;
      if (showAllLabel) {
          if (selectedValues?.length > 2) {
              let data = selectedValues.slice(0, 2).map((val) => val[labelKey]).join(", ");
              return `${data}, and ${selectedValues.length - 2} more`;
          } else {
              return selectedValues.map((val) => val[labelKey]).join(", ");
          }
      } else {
          return `All ${label}`;
      }
  };

  const renderOptionList = () => {
      const {
          loadingMessage = "Loading...",
          isObject,
          style = [],
          labelKey,
          labelIcon,
          primaryKey,
          noDataLabel = "No results found",
      } = props;
      if (loading) {
          return (
              <div className="loading-container">
                  <Loader />
                  {typeof loadingMessage === "string" && (
                      <span style={style["loadingMessage"]} className={`not--found`}>
                          {loadingMessage}
                      </span>
                  )}
              </div>
          );
      }
      return filteredLists.length > 0 ? (
          <>
              {selectAllOption && (
                  <li>
                      <div
                          key={"selAll"}
                          className={`drop--itemV2 ${allSelected ? "" : ""} ${cursor === -1 ? "" : ""
                              }`}
                          onClick={() => {
                              if (allSelected) {
                                  removeSelectAll();
                              } else {
                                  onSelectAll();
                              }
                          }}
                      >
                          <FormControlLabel
                              sx={{
                                  '&.Mui-checked': {
                                      color: '#004F9E !important',
                                  },
                                  '&.MuiFormControlLabel-root': {
                                      marginRight: '0px !important'
                                  }
                              }}
                              control={<Checkbox checked ={allSelected} />}
                          />
                          <Typography style={{ display: "flex", alignItems: "center" }}>
                              <span>Select All</span>
                          </Typography>
                      </div>
                  </li>
              )}
              {(optionLimit
                  ? filteredLists.slice(0, optionLimit)
                  : filteredLists
              ).map((el, index) => {
                  const isSelected = isSelectedValue(el);
                  const optionKey = isObject ? el[primaryKey] : `${el}-${index}`;
                  const optionLabel = isObject ? el[labelKey] : el;

                  return (
                      <li>
                          <div
                              key={optionKey}
                              className={`drop--item ${isSelected ? "selected" : ""} ${cursor === index ? "highlighted1" : ""
                                  }`}
                              onClick={(e) => onSelectItem(e, el)}
                          >
                              <FormControlLabel
                                  sx={{
                                      '&.Mui-checked': {
                                          color: '#004F9E !important',
                                      },
                                      '&.MuiFormControlLabel-root': {
                                          marginRight: '0px !important'
                                      }
                                  }}
                                  control={<Checkbox checked={isSelected} />}
                              />
                              <Typography
                                  style={{ display: "flex", alignItems: "center", fontWeight: '500', fontSize: '13px' }}>
                                  <span>{optionLabel}</span>
                              </Typography>
                          </div>
                      </li>
                  );
              })}
          </>
      ) : (
          <div className="not--found">{noDataLabel}</div>
      );
  };

  const labelTxyt = isCompulsory ? (
      <span>
          {label}
          <span style={{ color: "red", paddingLeft: "5px" }}>*</span>
      </span>
  ) : (
      label
  );

  const showSuggestion = (e, el) => {
      e.stopPropagation();
      setShowDrop(true);
  };

  const hideSuggestion = (e, el) => {
      setShowDrop(false);
  };

  const focusNextElement = (e) => {
      focusElemnt(nextId);
      e.preventDefault();
  };

  const focusPrevElement = () => {
      focusElemnt(prevId);
  };

  const keyboardNavigation1 = (e) => {
      if (e.key === "ArrowDown") {
          showSuggestion(e);
      }
      if (e.key === "ArrowUp") {
          showSuggestion(e);
      }
      if (e.key === "Escape") {
          focusPrevElement();
      }
      if (e.key === "Enter") {
          focusNextElement(e);
      }
  };

  const keyboardNavigation2 = (e) => {
      if (e.key === "ArrowDown") {
          showDrop
              ? setCursor((c) => (c < filteredLists.length - 1 ? c + 1 : -1))
              : showSuggestion();
      }

      if (e.key === "ArrowUp") {
          setCursor((c) => (c > 0 ? c - 1 : -1));
      }

      if (e.key === "Escape") {
          hideSuggestion();
          input1ref.current.focus();
      }

      if (e.key === "Enter" && cursor > -1) {
          const { onSelect = () => { }, singleSelect } = props;
          e.stopPropagation();
          const item = filteredLists[cursor];
          if (singleSelect) {
              onSingleSelect(item);
              onSelect([item], item);
              setAllSelected(false);
              focusNextElement(e);
              return;
          }
          if (isSelectedValue(item)) {
              onRemoveSelectedItem(item);
              return;
          }
          selectedValues.push(item);
          onSelect(selectedValues, item);
          setSelectedValues(Object.assign([], selectedValues));
          hideSuggestion();
          nextId ? focusNextElement(e) : input1ref.current.focus();
      }
  };

//   useEffect(() => {
//       if (portal) {
//           const dims = dropRef.current.getBoundingClientRect();
//           const style = {};
//           style.width = 'max-content';
//           style.top = direction === 'up' ? dims.top + window.scrollY - 210 : dims.top + window.scrollY + 42;
//           style.left = dims.left + window.scrollX;
//           setDropPortalStyle(style);
//       }
//   }, [dropRef, portal]);

  const dropDownRenderer = (props) => (
    //   <div className="drop--wrapper" 
    // //   style={!!portal ? dropPortalStyle : { }}
    //   >
    <>
          <div className="drop--search">
              <input
                  name={name}
                  type="text"
                  className="search"
                  placeholder={searchPlaceholder}
                  onChange={(e) => searchItems(e.target.value)}
                  onKeyDown={(e) => keyboardNavigation2(e)}
                  ref={searchInputRef}
                  autoFocus
                  autoComplete={"off"}
              />
              <div className="search--icon">
                  <FontAwesomeIcon icon={faSearch} color={" #777"} />
              </div>
              {(searchInput || remoteSearchInput) && (
                  <div className="cross--icon" onClick={clearSearchInput}>
                      <span className="cross">
                          <FontAwesomeIcon icon={faXmark} color={"#777"} />
                      </span>
                  </div>
              )}
          </div>
          <div className="drop--content scroll" style={{height:listHeight}} onScroll={handleScroll}>
              <ul className="list-group" ref={searchResultRef}>
                  {renderOptionList()}
              </ul>
          </div>
          </>
  );

  return (
      <div
          className={`dropdown--wrapper ${disabled ? "disable" : ""}`}
          style={{ width: width }}
          ref={dropRef}
          onClick={handleDrop}
      >
          <input
              type="text"
              className={`inp--field ${disabled ? "disable" : ""} ${showDrop ? "focusColor" : ""
                  }`}
              value={allSelected ? showAllLabel() : selectedDisplayValue}
              readOnly
              id={id}
              ref={input1ref}
              onKeyDown={keyboardNavigation1}
              onClick={showSuggestion}
          />
          {/* {selectedDisplayValue && !disabled && (
                  <div className="cross--icon" onClick={resetItem}>
                      <span className="cross">
                          <FontAwesomeIcon icon={faXmark} color={"#777"} />
                      </span>
                  </div>
              )} */}
          <div className="caret--icon">{showDrop ? <ArrowUp /> : <Arrow />}</div>
          <div
              className={`inp--label ${showDrop ? "focus--label" : ""} ${focus ? "active" : selectedDisplayValue && blur ? " filled" : ""
                  }`}
          >
              {labelTxyt}
          </div>
          {showDrop && (
            //   !!portal ? (
            //       <Portal container={document.getElementById('root')}>
            //           <div className="dropdown--wrapper dropdown--wrapper--portal" ref={portalRef} style={{ display: showDrop ? 'block' : 'none' }}>
            //               {dropDownRenderer()}
            //           </div>
            //       </Portal>
            //   ) : 
              (
                <div className="drop--wrapper1" style={{ width: listWidth, top: direction === 'up' ? 'auto' : '45px', bottom: direction === 'up' ? '100%' : 'auto' }}>
                      {dropDownRenderer()}
                  </div>
              )
          )}
      </div>
  );
});

export default MultiSelectDropDownV2;